import { Tooltip } from "devextreme-react/tooltip";
import React, { useId } from "react";

export interface IPhaseNameHeadingProps {
    name: string;
    // Click opens the phase summary. Pair with cursor="pointer" when the user is allowed to.
    onClick?: () => void;
    cursor?: string;
    // Hover tooltip content; omitted = no tooltip.
    tooltipText?: React.ReactNode;
    // Rendered inside the heading after the name (e.g. a due date).
    suffix?: React.ReactNode;
    // Rendered before the heading (e.g. the phase status icon).
    leading?: React.ReactNode;
    // Rendered after the heading (e.g. the templates-mode workflow pill).
    trailing?: React.ReactNode;
    headingClassName?: string;
    headingPaddingLeft?: string;
    // Merged over the heading's own style (the h6 is bold by default - callers matching a plain
    // label pass fontWeight: "normal").
    headingStyle?: React.CSSProperties;
}

// The clickable phase name, shared by the tasklist toolbar and the extended phase header so the
// name reads and behaves the same wherever it is shown.
export function PhaseNameHeading({
    name,
    onClick,
    cursor = "default",
    tooltipText,
    suffix,
    leading,
    trailing,
    headingClassName,
    headingPaddingLeft = "0px",
    headingStyle,
}: IPhaseNameHeadingProps) {
    // Stable per-instance id so the Tooltip attaches to the correct heading when several
    // render on the same module page.
    const reactId = useId();
    const tooltipTargetId = `tasklist-name-${reactId.replace(/[:]/g, "")}`;
    const showTooltip = !!tooltipText;

    return (
        <div style={{ display: "flex", alignItems: "center", cursor }} onClick={onClick}>
            {leading}
            <h6
                id={showTooltip ? tooltipTargetId : undefined}
                className={headingClassName}
                style={{ paddingLeft: headingPaddingLeft, whiteSpace: "nowrap", margin: 0, ...headingStyle }}
                data-testid={showTooltip ? "tasklist-name-workflow-tooltip" : undefined}
            >
                {name} {suffix}
            </h6>
            {trailing}
            {showTooltip && (
                <Tooltip
                    target={`#${tooltipTargetId}`}
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    hideOnOutsideClick={false}
                    position="top"
                >
                    <div data-testid="workflow-tooltip-content" style={{ padding: "4px 8px", maxWidth: "280px", textAlign: "left" }}>
                        {tooltipText}
                    </div>
                </Tooltip>
            )}
        </div>
    );
}
